import { isRecord } from '@/lib/guards';
import type { DateRange, PeriodPoint } from '@/utils';

import type {
  PeriodComparisonMode,
  PeriodComparisonPersistedState,
  PeriodComparisonResults,
  PeriodComparisonSettings,
  PeriodComparisonState,
} from './types';

const MONTHS_PER_YEAR = 12;

export const PERIOD_COMPARISON_MODES: PeriodComparisonMode[] = ['previousPeriod', 'samePeriodLastYear'];

export const DEFAULT_PERIOD_COMPARISON: PeriodComparisonSettings = {
  enabled: false,
  mode: 'samePeriodLastYear',
};

export function periodToTotalMonths(period: PeriodPoint) {
  return period.year * MONTHS_PER_YEAR + (period.month - 1);
}

export function totalMonthsToPeriod(totalMonths: number): PeriodPoint {
  const monthIndex = ((totalMonths % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR;

  return {
    month: monthIndex + 1,
    year: (totalMonths - monthIndex) / MONTHS_PER_YEAR,
  };
}

export function clampDateRange(range: DateRange, minMax?: DateRange): DateRange {
  if (!range.length || !minMax?.length) return range;

  const [min, max] = minMax;
  const minTotal = periodToTotalMonths(min);
  const maxTotal = periodToTotalMonths(max);
  const startTotal = Math.max(periodToTotalMonths(range[0]), minTotal);
  const endTotal = Math.min(periodToTotalMonths(range[1]), maxTotal);

  if (startTotal > endTotal) return [];

  return [totalMonthsToPeriod(startTotal), totalMonthsToPeriod(endTotal)];
}

function hasCompleteDateRange(range?: DateRange): range is [PeriodPoint, PeriodPoint] {
  return Boolean(range?.[0] && range?.[1]);
}

export function getComparisonRange(primaryRange: DateRange, mode: PeriodComparisonMode): DateRange {
  if (primaryRange.length < 2) return [];

  const [start, end] = primaryRange;
  const startTotal = periodToTotalMonths(start);
  const endTotal = periodToTotalMonths(end);
  const length = endTotal - startTotal + 1;

  if (mode === 'samePeriodLastYear') {
    return [totalMonthsToPeriod(startTotal - MONTHS_PER_YEAR), totalMonthsToPeriod(endTotal - MONTHS_PER_YEAR)];
  }

  return [totalMonthsToPeriod(startTotal - length), totalMonthsToPeriod(startTotal - 1)];
}

export function isDateRangeAvailable(range: DateRange, minMax?: DateRange): boolean {
  if (!hasCompleteDateRange(range) || !hasCompleteDateRange(minMax)) return false;

  const startTotal = periodToTotalMonths(range[0]);
  const endTotal = periodToTotalMonths(range[1]);

  return (
    startTotal <= endTotal && startTotal >= periodToTotalMonths(minMax[0]) && endTotal <= periodToTotalMonths(minMax[1])
  );
}

export function isComparisonRangeAvailable(
  primaryRange: DateRange,
  mode: PeriodComparisonMode,
  minMax?: DateRange
): boolean {
  return isDateRangeAvailable(getComparisonRange(primaryRange, mode), minMax);
}

export function deriveComparisonRange(
  primaryRange: DateRange,
  mode: PeriodComparisonMode,
  minMax?: DateRange
): DateRange {
  return clampDateRange(getComparisonRange(primaryRange, mode), minMax);
}

function isPeriodComparisonMode(value: unknown): value is PeriodComparisonMode {
  return PERIOD_COMPARISON_MODES.includes(value as PeriodComparisonMode);
}

export function sanitizePeriodComparison(value: unknown): PeriodComparisonSettings {
  if (!isRecord(value)) return DEFAULT_PERIOD_COMPARISON;

  return {
    enabled: value.enabled === true,
    mode: isPeriodComparisonMode(value.mode) ? value.mode : DEFAULT_PERIOD_COMPARISON.mode,
  };
}

export function toPersistedPeriodComparison({
  enabled,
  mode,
}: PeriodComparisonSettings): PeriodComparisonPersistedState | undefined {
  if (!enabled && mode === DEFAULT_PERIOD_COMPARISON.mode) return undefined;

  return { enabled, mode };
}

export function getPeriodComparisonState({
  dateRangeMinMax,
  primaryRange,
  results = {},
  settings,
}: {
  dateRangeMinMax?: DateRange;
  primaryRange: DateRange;
  results?: PeriodComparisonResults;
  settings: PeriodComparisonSettings;
}): PeriodComparisonState {
  const comparisonRange =
    settings.enabled && isComparisonRangeAvailable(primaryRange, settings.mode, dateRangeMinMax)
      ? deriveComparisonRange(primaryRange, settings.mode, dateRangeMinMax)
      : [];
  const ranges =
    settings.enabled && primaryRange.length && comparisonRange.length ? [primaryRange, comparisonRange] : [];

  return {
    ...settings,
    ranges,
    results,
  };
}
